/**
 * botUtils.js — Bot decision system (v1 / v2 / v3)
 */

import { AIBot } from './aiBot.js';
import {
  getAvailablePositions,
  getNeighborValues,
  deltaScore,
  expansionFrontierAfterPlace,
  positionExpansionPotential,
  countIslandsTouching,
  getScoreRanking,
  detectGamePhase,
  applyMoveToState,
} from './botHelpers.js';

// ---------------------------------------------------------------------------
// 1. Deterministic seed from username
// ---------------------------------------------------------------------------

/**
 * djb2 hash — fast, deterministic, no external deps.
 * Same nick → same seed → same bot "style" across all games.
 *
 * @param {string} username
 * @returns {number} non-negative 32-bit integer
 */
export function hashUsername(username) {
  let hash = 5381;
  for (let i = 0; i < username.length; i++) {
    hash = ((hash << 5) + hash) + username.charCodeAt(i);
    hash = hash & hash; // force 32-bit int
  }
  return Math.abs(hash);
}

// ---------------------------------------------------------------------------
// 2. Seeded PRNG (Mulberry32)
// ---------------------------------------------------------------------------

/**
 * Creates a seeded pseudo-random number generator (Mulberry32).
 * Returns a function that generates numbers in [0, 1) — drop-in for Math.random().
 *
 * @param {number} seed — non-negative integer (use hashUsername output)
 * @returns {() => number}
 */
export function createSeededRng(seed) {
  let s = seed >>> 0;
  return function () {
    s = Math.imul(s ^ (s >>> 15), 1 | s);
    s ^= s + Math.imul(s ^ (s >>> 7), 61 | s);
    return ((s ^ (s >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Creates a mixed RNG that blends a seeded personality component with
 * real Math.random() randomness, preventing deterministic loops.
 *
 * The `weight` controls how much personality vs chaos is mixed in:
 *   weight=0.5 → equal blend (default)
 *   weight=0.7 → 70% seeded, 30% random (more "personality")
 *   weight=0.3 → 30% seeded, 70% random (more chaotic)
 *
 * The result is always in [0, 1) and avoids degenerate fixed-point sequences
 * because Math.random() injects fresh entropy every call. The nickname seed
 * biases which branches and tie-breaks a bot tends to prefer over many games,
 * but does not force a fully deterministic move loop.
 *
 * @param {number} seed — non-negative integer
 * @param {number} weight — 0–1, how much of the seeded stream to mix in
 * @returns {() => number}
 */
export function createBotRng(seed, weight = 0.5) {
  const seeded = createSeededRng(seed);
  return function () {
    return seeded() * weight + Math.random() * (1 - weight);
  };
}

export const BOT_RNG_WEIGHTS = {
  v1: 0.3,
  v2: 0.4,
  v3: 0.3,
};

// Bot strength comes from two layers working together:
// 1. persona-specific heuristics (v1 / v2 / v3 decision logic)
// 2. username-seeded RNG bias shaping how consistently that persona follows
//    one style vs. injecting extra chaos across repeated games.

// ---------------------------------------------------------------------------
// 3. Persona dispatcher
// ---------------------------------------------------------------------------

export function makeDecision(gameState, botProfile, playerIndex, colorIndex, rngOverride) {
  const persona = botProfile?.bot_persona ?? 'v1';
  const seed = hashUsername(botProfile?.username ?? 'default');

  switch (persona) {
    case 'v3': {
      const rng = rngOverride ?? createBotRng(seed, BOT_RNG_WEIGHTS.v3);
      return v3BotDecision(gameState, playerIndex, colorIndex, rng);
    }
    case 'v2': {
      const rng = rngOverride ?? createBotRng(seed, BOT_RNG_WEIGHTS.v2);
      return v2BotDecision(gameState, playerIndex, colorIndex, rng);
    }
    case 'v1':
    default: {
      const rng = rngOverride ?? createBotRng(seed, BOT_RNG_WEIGHTS.v1);
      return v1BotDecision(gameState, playerIndex, colorIndex, rng);
    }
  }
}

export async function makeDecisionAsync(gameState, botProfile, playerIndex, colorIndex, rngOverride) {
  return makeDecision(gameState, botProfile, playerIndex, colorIndex, rngOverride);
}

function v1BotDecision(gameState, playerIndex, colorIndex, rng) {
  const bot = new AIBot(playerIndex, colorIndex, 'v1', rng);
  return bot.makeMove(gameState);
}

/**
 * v2 "Greedy Chaos" bot.
 *
 * Design goal: stop overvaluing swaps and play more direct turns.
 *   1. If it has its own color, it always places it.
 *   2. If it does not, it swaps only when the target hand is genuinely rich in its color.
 *   3. Otherwise it places a random-ish opponent marker, biased toward low-help spots.
 *
 * Differentiation from v1 / v3:
 *   - much stronger own-color priority than v1
 *   - much less swap-happy than v1
 *   - less optimized than v3, with deliberate top-bucket randomness
 */
function v2BotDecision(gameState, playerIndex, colorIndex, rng) {
  const { players, gameBoard, markersPool } = gameState;
  const canSwap = gameState.canSwap !== false;
  const swapsAreLimited = typeof gameState.swapLimitPerPlayer === 'number' && gameState.swapLimitPerPlayer > 0;
  const myMarkers = players[playerIndex].markers;
  const phase = detectGamePhase(gameState);

  if (myMarkers.length === 0) return null;

  const availablePositions = getAvailablePositions(gameBoard);
  if (availablePositions.length === 0) return null;

  // Pre-compute: who is the leader?
  const ranking = getScoreRanking(gameBoard, players);
  const leaderIdx = ranking[0].playerIndex;
  const iAmLeader = leaderIdx === playerIndex;

  const myColorIndexes = myMarkers
    .map((marker, idx) => marker.colorIndex === colorIndex ? idx : -1)
    .filter(idx => idx !== -1);

  if (myColorIndexes.length > 0) {
    const markerIndex = myColorIndexes[Math.floor(rng() * myColorIndexes.length)];
    const position = _v2PickGreedyOwnPlacement(gameState, playerIndex, markerIndex, colorIndex, availablePositions, phase, rng);
    return { action: 'place', markerIndex, position };
  }

  if (canSwap && markersPool.length > 0) {
    const swapTarget = _v2FindSwapTarget(players, playerIndex, colorIndex, leaderIdx, iAmLeader, rng);
    if (swapTarget !== -1) {
      const theirMyColors = players[swapTarget].markers.filter(
        m => m.colorIndex === colorIndex
      ).length;
      const shouldSwap = _v2ShouldSwap(theirMyColors, phase, swapsAreLimited, rng);
      if (shouldSwap) {
        return { action: 'swap', targetPlayerId: swapTarget };
      }
    }
  }

  return _v2PickChaoticFallback(gameBoard, myMarkers, colorIndex, players, leaderIdx, playerIndex, phase, rng);
}

function _v2ShouldSwap(theirMyColors, phase, swapsAreLimited, rng) {
  if (theirMyColors <= 0) return false;

  if (swapsAreLimited) {
    if (phase === 'opening') return theirMyColors >= 3;
    return theirMyColors >= 2;
  }

  if (phase === 'opening') {
    return theirMyColors >= 3
      || (theirMyColors === 2 && rng() < 0.12);
  }

  if (phase === 'endgame') {
    return theirMyColors >= 2
      || (theirMyColors === 1 && rng() < 0.35);
  }

  return theirMyColors >= 3
    || (theirMyColors === 2 && rng() < 0.45)
    || (theirMyColors === 1 && rng() < 0.12);
}

/**
 * Score own-color placements with stronger greed than v1, but still keep
 * some randomness by selecting from a small top bucket instead of the single
 * best position every time.
 * @private
 */
function _v2PickGreedyOwnPlacement(gameState, playerIndex, markerIndex, colorIndex, positions, phase, rng) {
  if (positions.length === 0) return null;

  const { gameBoard } = gameState;

  const isOpening = phase === 'opening';
  const isEndgame = phase === 'endgame';

  const scored = positions.map(pos => {
    const { row, col } = pos;
    const delta = deltaScore(gameBoard, row, col, colorIndex);
    let score = delta * (isEndgame ? 24 : isOpening ? 14 : 18);

    const frontier = expansionFrontierAfterPlace(gameBoard, row, col, colorIndex);
    score += frontier * (isOpening ? 3.2 : isEndgame ? 1.2 : 2);

    const { emptyNeighbors, wallNeighbors } = positionExpansionPotential(gameBoard, row, col);
    score += wallNeighbors * (isOpening ? 2.2 : 1.5);
    score += emptyNeighbors * (isOpening ? 1.6 : 0.8);

    const islands = countIslandsTouching(gameBoard, row, col, colorIndex);
    if (islands >= 2) score += isEndgame ? 20 : 14;

    const neighbors = getNeighborValues(gameBoard, row, col);
    const myNeighbors = neighbors.filter(n => n === colorIndex).length;
    score += myNeighbors * (isEndgame ? 5 : 4);

    const enemyNeighbors = neighbors.filter(n => typeof n === 'number' && n !== colorIndex).length;
    score -= enemyNeighbors * (isEndgame ? 1.8 : 1.4);

    score -= _v2EstimateNextPlayerCounterplay(gameState, playerIndex, markerIndex, pos, phase);

    if (isOpening && delta === 0 && frontier >= 3) score += 6;
    if (frontier <= 1 && delta < (isEndgame ? 6 : 4)) score -= isOpening ? 10 : isEndgame ? 3 : 6;

    score += rng() * 5;
    return { ...pos, score };
  });

  scored.sort((a, b) => b.score - a.score);
  const bestScore = scored[0].score;
  const topBucket = scored.filter(pos => pos.score >= bestScore - 5).slice(0, 3);
  const choice = topBucket[Math.floor(rng() * topBucket.length)] || scored[0];
  return { row: choice.row, col: choice.col };
}

function _v2EstimateNextPlayerCounterplay(gameState, playerIndex, markerIndex, position, phase) {
  const simulatedState = applyMoveToState(gameState, {
    action: 'place',
    markerIndex,
    position,
  }, playerIndex);

  const nextPlayerIndex = simulatedState.currentPlayer;
  const nextPlayer = simulatedState.players[nextPlayerIndex];
  if (!nextPlayer || nextPlayer.markers.length === 0) return 0;

  const nextDecision = v3BotDecision(simulatedState, nextPlayerIndex, nextPlayer.colorIndex, () => 0);
  if (!nextDecision) return 0;

  if (nextDecision.action === 'place' && nextDecision.position) {
    const responseMarker = simulatedState.players[nextPlayerIndex].markers[nextDecision.markerIndex];
    if (!responseMarker) return 0;

    const responseColor = responseMarker.colorIndex;
    const responseDelta = deltaScore(
      simulatedState.gameBoard,
      nextDecision.position.row,
      nextDecision.position.col,
      responseColor
    );
    const responseFrontier = expansionFrontierAfterPlace(
      simulatedState.gameBoard,
      nextDecision.position.row,
      nextDecision.position.col,
      responseColor
    );
    const responseBridges = countIslandsTouching(
      simulatedState.gameBoard,
      nextDecision.position.row,
      nextDecision.position.col,
      responseColor
    );

    let penalty = responseDelta * (phase === 'opening' ? 24 : phase === 'endgame' ? 18 : 21);
    penalty += responseFrontier * (phase === 'opening' ? 2.4 : phase === 'endgame' ? 1.6 : 2.0);

    if (responseBridges >= 2) {
      penalty += 10 + (responseBridges - 2) * 5;
    }

    if (responseColor === nextPlayer.colorIndex) {
      penalty += phase === 'opening' ? 8 : 5;
    }

    return penalty;
  }

  if (nextDecision.action === 'swap') {
    const ownColorBefore = nextPlayer.markers.filter(m => m.colorIndex === nextPlayer.colorIndex).length;
    const targetMarkers = simulatedState.players[nextDecision.targetPlayerId]?.markers ?? [];
    const ownColorAfter = targetMarkers.filter(m => m.colorIndex === nextPlayer.colorIndex).length;
    const gainedOwnColors = Math.max(0, ownColorAfter - ownColorBefore);
    return gainedOwnColors > 0
      ? gainedOwnColors * (phase === 'opening' ? 8 : phase === 'endgame' ? 10 : 9)
      : 4;
  }

  return 0;
}

/**
 * Swap target: same logic as v1/v3 — find player with most of our colors,
 * with anti-kingmaking tiebreaking.
 * @private
 */
function _v2FindSwapTarget(players, currentPlayer, colorIndex, leaderIdx, iAmLeader, rng) {
  let maxMyColors = 0;
  const candidateTargets = [];

  players.forEach((player, idx) => {
    if (idx === currentPlayer) return;
    const myColorsCount = player.markers.filter(m => m.colorIndex === colorIndex).length;

    if (myColorsCount > maxMyColors) {
      maxMyColors = myColorsCount;
      candidateTargets.length = 0;
      candidateTargets.push(idx);
    } else if (myColorsCount === maxMyColors && myColorsCount > 0) {
      candidateTargets.push(idx);
    }
  });

  if (candidateTargets.length === 0) return -1;

  // Among tied candidates, prefer not swapping with the leader
  if (candidateTargets.length > 1) {
    const myMarkers = players[currentPlayer].markers;
    const scored = candidateTargets.map(idx => {
      const theirColor = players[idx].colorIndex;
      const theirColorInMyHand = myMarkers.filter(m => m.colorIndex === theirColor).length;
      let penalty = theirColorInMyHand * 3;
      if (idx === leaderIdx && !iAmLeader) penalty += 5;
      return { idx, penalty };
    });
    scored.sort((a, b) => a.penalty - b.penalty);
    const bestPenalty = scored[0].penalty;
    const topCandidates = scored.filter(s => s.penalty <= bestPenalty + 2);
    return topCandidates[Math.floor(rng() * topCandidates.length)].idx;
  }

  return candidateTargets[0];
}

/**
 * Chaotic fallback placement for non-own markers.
 *
 * Instead of hard-optimizing one line, evaluate many marker/position pairs,
 * bias toward low-help placements, then randomly choose from the best few.
 * @private
 */
function _v2PickChaoticFallback(gameBoard, myMarkers, myColorIndex, players, leaderIdx, playerIndex, phase, rng) {
  const availablePositions = getAvailablePositions(gameBoard);
  if (availablePositions.length === 0) return null;

  const isOpening = phase === 'opening';
  const isEndgame = phase === 'endgame';

  const scoredOptions = [];

  myMarkers.forEach((marker, markerIndex) => {
    availablePositions.forEach(pos => {
      const { row, col } = pos;
      const ownerIdx = players.findIndex(p => p.colorIndex === marker.colorIndex);
      const isLeaderColor = ownerIdx === leaderIdx && ownerIdx !== playerIndex;
      const delta = deltaScore(gameBoard, row, col, marker.colorIndex);
      const { emptyNeighbors, wallNeighbors } = positionExpansionPotential(gameBoard, row, col);
      const neighbors = getNeighborValues(gameBoard, row, col);
      const sameColorNeighbors = neighbors.filter(n => n === marker.colorIndex).length;
      const anyColorNeighbors = neighbors.filter(n => typeof n === 'number').length;
      const myNeighbors = neighbors.filter(n => n === myColorIndex).length;

      let score = 0;
      score -= delta * (isEndgame ? 18 : isOpening ? 10 : 14);
      score -= sameColorNeighbors * (isEndgame ? 12 : 10);
      score -= anyColorNeighbors * (isEndgame ? 2.2 : isOpening ? 1.0 : 1.5);
      score += (4 - emptyNeighbors) * (isEndgame ? 3.0 : isOpening ? 1.6 : 2.2);
      score += wallNeighbors * (isOpening ? 1.0 : 1.5);
      score += myNeighbors * (isOpening ? 0.6 : isEndgame ? -1.5 : 1.2);

      if (delta === 0) score += isEndgame ? 6 : 4;
      if (isLeaderColor) {
        score -= delta * (isEndgame ? 12 : 8);
        if (delta === 0) score += isEndgame ? 6 : 4;
      }

      score += rng() * 8;

      scoredOptions.push({ markerIndex, row, col, score });
    });
  });

  scoredOptions.sort((a, b) => b.score - a.score);
  const bestScore = scoredOptions[0]?.score ?? 0;
  const topBucket = scoredOptions.filter(option => option.score >= bestScore - 6).slice(0, 6);
  const choice = topBucket[Math.floor(rng() * topBucket.length)] || scoredOptions[0];

  return {
    action: 'place',
    markerIndex: choice.markerIndex,
    position: { row: choice.row, col: choice.col }
  };
}

// ---------------------------------------------------------------------------
// 3b. v3 Bot — v1 decision flow with enhanced evaluation
// ---------------------------------------------------------------------------

/**
 * v3 bot — follows v1's priority-chain decision flow exactly:
 *   1. Have own color? → place it at best position (90%) / skip (10%)
 *   2. Good swap target? → swap (90%) / skip (10%)
 *   3. Fallback → place any marker at safest spot
 *
 * But with smarter evaluation at each step:
 *   - Better own-color placement (island bridging, expansion frontier, dead-end avoidance)
 *   - Smarter swap target (net benefit, anti-kingmaking)
 *   - Smarter opponent placement (anti-kingmaking, toxicity-aware isolation)
 */
function v3BotDecision(gameState, playerIndex, colorIndex, rng) {
  const { players, gameBoard, markersPool } = gameState;
  const canSwap = gameState.canSwap !== false;
  const swapsAreLimited = typeof gameState.swapLimitPerPlayer === 'number' && gameState.swapLimitPerPlayer > 0;
  const myMarkers = players[playerIndex].markers;

  if (myMarkers.length === 0) return null;

  const availablePositions = getAvailablePositions(gameBoard);
  if (availablePositions.length === 0) return null;

  // --- Pre-compute rankings and leader info ---
  const ranking = getScoreRanking(gameBoard, players);
  const leaderIdx = ranking[0].playerIndex;
  const iAmLeader = leaderIdx === playerIndex;

  // Build ordered list of candidate actions (same as v1)
  const actions = [];

  // Strategy 1: Do I have my own color? → place it at the BEST spot
  const myColorMarkerIndex = myMarkers.findIndex(m => m.colorIndex === colorIndex);
  if (myColorMarkerIndex !== -1) {
    actions.push(() => {
      const bestPosition = _v3FindBestOwnPosition(gameBoard, colorIndex, players, leaderIdx, iAmLeader, rng);
      return {
        action: 'place',
        markerIndex: myColorMarkerIndex,
        position: bestPosition
      };
    });
  }

  // Strategy 2: Swap with best target (smarter target selection than v1)
  if (canSwap && markersPool.length > 0) {
    const bestSwapTarget = _v3FindBestSwapTarget(players, playerIndex, colorIndex, leaderIdx, iAmLeader, rng);
    if (bestSwapTarget !== -1) {
      const theirMyColors = players[bestSwapTarget].markers.filter(
        m => m.colorIndex === colorIndex
      ).length;
      // Same threshold as v1: 2+ always swap, 1 with 70% chance — but when limited, require 2+
      const shouldSwap = swapsAreLimited
        ? theirMyColors >= 2
        : theirMyColors >= 2 || (theirMyColors === 1 && rng() < 0.7);
      if (shouldSwap) {
        actions.push(() => ({
          action: 'swap',
          targetPlayerId: bestSwapTarget
        }));
      }
    }
  }

  // Strategy 3: Place any marker at safest spot (enhanced safe placement)
  actions.push(() => {
    const randomMarkerIndex = Math.floor(rng() * myMarkers.length);
    const markerColor = myMarkers[randomMarkerIndex].colorIndex;
    const safePosition = _v3FindSafePosition(gameBoard, markerColor, players, leaderIdx, iAmLeader, rng);
    return {
      action: 'place',
      markerIndex: randomMarkerIndex,
      position: safePosition
    };
  });

  // Execute with 90/10 skip randomness (same as v1)
  for (let i = 0; i < actions.length; i++) {
    const isLastAction = i === actions.length - 1;
    if (isLastAction || rng() <= 0.9) {
      return actions[i]();
    }
  }

  // Fallback (should never reach here)
  return actions[actions.length - 1]();
}

/**
 * Enhanced own-color placement: v1's evaluatePosition + island bridging,
 * expansion frontier, dead-end avoidance, and leader blocking.
 * @private
 */
function _v3FindBestOwnPosition(gameBoard, colorIndex, players, leaderIdx, iAmLeader, rng) {
  const availablePositions = getAvailablePositions(gameBoard);
  if (availablePositions.length === 0) return null;

  const scoredPositions = availablePositions.map(pos => {
    const { row, col } = pos;

    // Base: real delta score (same as v1's evaluatePosition)
    const delta = deltaScore(gameBoard, row, col, colorIndex);
    let score = delta * 10;

    // Island bridging bonus: connecting 2+ separate islands is a huge score jump
    const islandsTouching = countIslandsTouching(gameBoard, row, col, colorIndex);
    if (islandsTouching >= 2) {
      score += 15 + (islandsTouching - 2) * 6;
    }

    // Expansion frontier: prefer positions that open up future placement
    const frontier = expansionFrontierAfterPlace(gameBoard, row, col, colorIndex);
    score += frontier * 1.5;

    // Dead-end penalty: avoid corridors with ≤1 exit unless the score gain is huge
    if (frontier <= 1 && delta < 4) {
      score -= 10;
    }

    // Wall neighbors open up future Y cells (same idea as v1)
    const { wallNeighbors } = positionExpansionPotential(gameBoard, row, col);
    score += wallNeighbors * 1.5;

    // Leader blocking: slight bonus for placing near leader's frontier
    if (!iAmLeader) {
      const leaderColor = players[leaderIdx].colorIndex;
      const leaderNeighbors = _countColorNeighbors(gameBoard, row, col, leaderColor);
      if (leaderNeighbors > 0) {
        score += leaderNeighbors * 2;
      }
    }

    // Unpredictability (same scale as v1)
    score += rng() * 4;

    return { ...pos, score };
  });

  scoredPositions.sort((a, b) => b.score - a.score);
  const best = scoredPositions[0];
  return { row: best.row, col: best.col };
}

/**
 * Enhanced swap target selection: v1's "player with most of my colors" but
 * with net-benefit tiebreaking and anti-kingmaking.
 * @private
 */
function _v3FindBestSwapTarget(players, currentPlayer, colorIndex, leaderIdx, iAmLeader, rng) {
  let maxMyColors = 0;
  const candidateTargets = [];

  players.forEach((player, idx) => {
    if (idx === currentPlayer) return;

    const myColorsCount = player.markers.filter(m => m.colorIndex === colorIndex).length;

    if (myColorsCount > maxMyColors) {
      maxMyColors = myColorsCount;
      candidateTargets.length = 0;
      candidateTargets.push(idx);
    } else if (myColorsCount === maxMyColors && myColorsCount > 0) {
      candidateTargets.push(idx);
    }
  });

  if (candidateTargets.length === 0) return -1;

  // Among tied candidates, prefer the one with the best net benefit for us
  // (give them less of their own color, avoid helping the leader)
  if (candidateTargets.length > 1) {
    const myMarkers = players[currentPlayer].markers;

    const scored = candidateTargets.map(idx => {
      const theirColor = players[idx].colorIndex;
      const theirColorInMyHand = myMarkers.filter(m => m.colorIndex === theirColor).length;
      let penalty = theirColorInMyHand * 3; // penalize giving them their own color

      // Anti-kingmaking: extra penalty for swapping with the leader
      if (idx === leaderIdx && !iAmLeader) {
        penalty += 5;
      }

      return { idx, penalty };
    });

    scored.sort((a, b) => a.penalty - b.penalty);

    // Among best-scored (within 2 penalty), pick randomly
    const bestPenalty = scored[0].penalty;
    const topCandidates = scored.filter(s => s.penalty <= bestPenalty + 2);
    return topCandidates[Math.floor(rng() * topCandidates.length)].idx;
  }

  return candidateTargets[0];
}

/**
 * Enhanced safe position for opponent markers: v1's isolation logic but
 * with anti-kingmaking awareness.
 * @private
 */
function _v3FindSafePosition(gameBoard, markerColorIndex, players, leaderIdx, iAmLeader, rng) {
  const availablePositions = getAvailablePositions(gameBoard);
  if (availablePositions.length === 0) return null;

  // Find if this color belongs to the leader
  const ownerIdx = players.findIndex(p => p.colorIndex === markerColorIndex);
  const isLeaderColor = ownerIdx === leaderIdx && !iAmLeader;

  const scoredPositions = availablePositions.map(pos => {
    const { row, col } = pos;
    let score = 0;

    const delta = deltaScore(gameBoard, row, col, markerColorIndex);

    // Heavily penalize growing opponent island (same idea as v1 but using real delta)
    score -= delta * 20;

    // Prefer completely isolated placement
    if (delta === 0) score += 5;

    // Anti-kingmaking: extra penalty for growing the leader's island
    if (isLeaderColor) {
      score -= delta * 8;
      if (delta === 0) score += 4;
    }

    // Penalize being near any other color clusters (same as v1)
    const neighbors = _getNeighborValues(gameBoard, row, col);
    const anyColorNeighbors = neighbors.filter(n => typeof n === 'number').length;
    score -= anyColorNeighbors * 3;

    // Unpredictability
    score += rng() * 3;

    return { ...pos, score };
  });

  scoredPositions.sort((a, b) => b.score - a.score);
  const best = scoredPositions[0];
  return { row: best.row, col: best.col };
}

/**
 * Count how many 4-directional neighbors of (row, col) are a specific color.
 * @private
 */
function _countColorNeighbors(gameBoard, row, col, colorIndex) {
  const rows = gameBoard.length;
  const cols = gameBoard[0].length;
  let count = 0;
  for (const [dy, dx] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
    const nr = row + dy, nc = col + dx;
    if (nr >= 0 && nr < rows && nc >= 0 && nc < cols && gameBoard[nr][nc] === colorIndex) {
      count++;
    }
  }
  return count;
}

/** @private — thin alias so v3 safe-position can read neighbor values */
function _getNeighborValues(gameBoard, row, col) {
  return getNeighborValues(gameBoard, row, col);
}

// ---------------------------------------------------------------------------
// 4. Validation helper — used in registration form
// ---------------------------------------------------------------------------

/**
 * Returns true if the username is forbidden for human players
 * (ends with *BOT, case-insensitive).
 *
 * @param {string} username
 * @returns {boolean}
 */
export function hasBotSuffix(username) {
  return /\*BOT$/i.test(username.trim());
}
